import { useRequestHeaders } from '#app';
import { GetStore } from '../build/compile';

import {
  logInfoRequest,
  withDebugBuild,
  buildUniqueId,
  originDomain,
  currentLocale,
} from './required';

import { useUserDataStore } from '@/stores/userData';
import { useAuthStore } from '@/stores/auth';

export interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

interface ApiServiceOptions {
  slug?: string;
  server?: boolean;
  lazy?: boolean;
  api?: boolean;
}

// variable to define the user's access origin (app, browser, etc)
let originUserAccess: any = null;

const originUserAccessEnums = {
  undefined: 0,
  app: 1,
  desktop: 2,
  desktopApp: 3,
  mobile: 4,
  mobileApp: 5,
};

function getDebugStatus(): boolean {
  try {
    const { $debug } = useNuxtApp();

    return $debug?.isEnabled ?? false;
  } catch (error) {
    // Fallback caso o contexto Nuxt não esteja disponível
    return false;
  }
}

class ApiService {
  private static validatePath(path: string) {
    if (!path.match(/^\//)) {
      return `/${path}`;
    }

    return path;
  }

  private static getCommonHeaders() {
    const cookies = useRequestHeaders(['Cookie']);

    let locale;
    let finalLocale;

    try {
      const lang_cookie = useCookie('current_lang');

      if (lang_cookie.value) {
        locale = lang_cookie.value;
      } else if (currentLocale()) {
        locale = currentLocale();
      } else {
        locale = useRuntimeConfig().public.brand.language;
      }

      finalLocale = locale.replace('-mx', '');
    } catch (error) {
      locale = useRuntimeConfig().public.brand.language;
      finalLocale = useRuntimeConfig().public.brand.language;
    }

    let accountHeaders;
    const userData = useUserDataStore();

    const getCity = () => {
      const city = Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone.split('/')[1];

      return city || '';
    };

    if (userData.user) {
      accountHeaders = userData.getAccountHeaders();
    } else {
      accountHeaders = {
        city: userData.treatVariable(getCity()) ?? '',
      };
    }

    const baseDomain = originDomain();
    let versionId = buildUniqueId();
    const tenant = originDomain() || window.location.hostname || '';

    if (versionId) {
      versionId = 'vz3b-' + versionId;
    } else {
      versionId = 'vm';
    }

    const authStore = useAuthStore();

    const headers: any = {
      Accept: 'application/json',
      tenant,
      lang: finalLocale,
      language: finalLocale,
      version: versionId,
      Authorization: `Bearer ${authStore.accessToken}`,
      'origin-domain': baseDomain,
      ...cookies,
      ...accountHeaders,
    };

    try {
      if (withDebugBuild()) {
        if (location.href.includes('cactus-debug')) {
          headers['cactus-service'] = 'DEBUG';
        }
      }
    } catch (ex) {}

    try {
      if (originUserAccess === null) {
        const isApp = window.matchMedia('(display-mode: standalone)').matches;
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);

        if (isApp) {
          if (isMobile) {
            originUserAccess = originUserAccessEnums.mobileApp;
          } else {
            originUserAccess = originUserAccessEnums.desktopApp;
          }
        } else if (isMobile) {
          originUserAccess = originUserAccessEnums.mobile;
        } else {
          originUserAccess = originUserAccessEnums.desktop;
        }
      }
    } catch (ex) {
      originUserAccess = originUserAccessEnums.undefined;
    }
    if (originUserAccess) {
      headers['X-ORIGIN-ACCESS'] = originUserAccess;
    }

    return headers;
  }

  private static getExternalHeaders() {
    const headers: any = {
      Accept: 'application/json',
    };

    return headers;
  }

  // eslint-disable-next-line
  private static async sendRequest<T = any>(
    method: string,
    url: string,
    payload?: any,
    external?: boolean,
  ): Promise<ApiResponse<T>> {
    // eslint-disable-next-line
    return new Promise<ApiResponse<T>>(async (resolve, reject) => {
      try {
        const isDebugEnabled = getDebugStatus();

        if (!isDebugEnabled) {
          const fromBuild = await GetStore(method, url);

          if (fromBuild && fromBuild.has) {
            let headersToUse = {};

            // useDefaultHeaders
            if (fromBuild.headers) {
              headersToUse = fromBuild.headers;
              // coloco como "d" para economizar bytes no build
              if (headersToUse === 'd') {
                headersToUse = {
                  'cache-control': 'max-age=0, no-cache, must-revalidate',
                  'content-type': 'application/json',
                };
              }
            }

            const responseData = {
              data: fromBuild.data,
              status: fromBuild.status,
              statusText: fromBuild.statusText,
              headers: headersToUse,
            };

            if (responseData.status.toString().startsWith('20')) {
              // @ts-ignore
              return resolve(responseData);
            }
          }
        }

        $fetch(url, {
          onRequest({ options }: any) {
            options.method = method;
            if (external) {
              options.headers = new Headers(ApiService.getExternalHeaders());
            } else {
              options.headers = new Headers(ApiService.getCommonHeaders());
            }
            try {
              const documentReferrer =
                sessionStorage.getItem('c_documentReferrer') ||
                sessionStorage.getItem('c_documentReferrerInit') ||
                localStorage.getItem('c_documentReferrerLocal') ||
                localStorage.getItem('c_documentReferrerInitLocal') ||
                document.referrer;

              if (documentReferrer && !external) {
                let docRefHostname = documentReferrer;

                try {
                  docRefHostname = new URL(documentReferrer).hostname;
                } catch (ex) {}

                if (docRefHostname !== new URL(location.href).hostname) {
                  options.headers.set('X-ORIGIN-REFERRER', documentReferrer);
                  options.headers.set('X-ORIGIN-HOSTNAME', docRefHostname);

                  try {
                    const cInfo = localStorage.getItem('c_infoId');

                    if (cInfo) {
                      options.headers.set('X-ORIGIN-CINFO-ID', cInfo);
                    }
                  } catch (ex) {}

                  try {
                    const cInfoRef = sessionStorage.getItem('c_infoIdRef');

                    if (cInfoRef) {
                      options.headers.set('X-ORIGIN-CINFO-ID-REF', cInfoRef);
                    }
                  } catch (ex) {}
                }
              }
            } catch (ex) {}

            try {
              if (!external) {
                options.headers.set('X-LOG-INFO', logInfoRequest());
              }
            } catch (ex) {}
            if (payload) {
              options.body = payload;
            }
          },
          onRequestError({ error }) {
            reject(error);
          },
          onResponse({ response }) {
            const responseData = {
              data: response._data,
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            };

            try {
              const { $auth } = useNuxtApp();
              const { setUserExceedTimeoutLimit } = useUserLimitsStore();

              if (
                response.status === 202 &&
                $auth.isAnUnauthorizedEndpoint(url)
              ) {
                setUserExceedTimeoutLimit(true);
              } else {
                setUserExceedTimeoutLimit(false);
              }
            } catch (ex) {
              console.error('Critical files modified:');
              console.error(ex);
            }

            if (response.status.toString().startsWith('20')) {
              resolve(responseData);
            } else {
              reject(responseData);
            }
          },
          async onResponseError({ response }) {
            reject(response);

            const { status, url } = response;

            try {
              const { logout } = useAuthStore();
              const { $auth } = useNuxtApp();
              const { openChallenge } = useLayoutStore();

              if (status === 401 && $auth.isAnUnauthorizedEndpoint(url)) {
                await logout({
                  displayMessage: true,
                  redirectToHome: true,
                });

                const { isCheckInactivityEnabled } = useFeatureFlagsJson();

                if (isCheckInactivityEnabled.value) {
                  const { setLoggedOutByInactivity } = useUserActivityStore();

                  setLoggedOutByInactivity(true);
                }
              }

              if (status === 429) {
                openChallenge();
              }
            } catch (ex) {
              console.error('Critical files modified:');
              console.error(ex);
            }
          },
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  public static query<T = any>(resource: string): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'GET',
      `/api${ApiService.validatePath(resource)}`,
    );
  }

  public static get<T = any>(
    resource: string,
    options: ApiServiceOptions = {
      slug: '',
      server: true,
      lazy: true,
    },
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'GET',
      `/api${ApiService.validatePath(resource)}${
        options.slug ? '/' + options.slug : ''
      }`,
    );
  }

  public static post<T = any>(
    resource: string,
    payload: any,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'POST',
      `/api${ApiService.validatePath(resource)}`,
      payload,
    );
  }

  public static update<T = any>(
    resource: string,
    slug: string,
    payload: any,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'PUT',
      `/api${ApiService.validatePath(resource)}/${slug}`,
      payload,
    );
  }

  public static put<T = any>(
    resource: string,
    payload: any,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'PUT',
      `/api${ApiService.validatePath(resource)}`,
      payload,
    );
  }

  public static patch<T = any>(
    resource: string,
    payload: any,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'PATCH',
      `/api${ApiService.validatePath(resource)}`,
      payload,
    );
  }

  public static delete<T = any>(
    resource: string,
    payload: any,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest(
      'DELETE',
      `/api${ApiService.validatePath(resource)}`,
      payload,
    );
  }

  public static get_external<T = any>(
    resource: string,
  ): Promise<ApiResponse<T>> {
    return ApiService.sendRequest('GET', `${resource}`, undefined, true);
  }
}

export default ApiService;
